import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, UserPlus } from "lucide-react";

/**
 * Friends — dedicated list of the caller's friends, alphabetical (backend
 * sorts by display name). Jump-to-profile via each card. Empty state
 * gently nudges toward the directory.
 */
export default function Friends() {
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await api.get("/commons/friends");
        if (!cancel) setFriends(r.data.friends || []);
      } catch (e) {
        console.error("[friends] load failed:", e);
        if (!cancel) setFriends([]);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 pt-8 pb-24" data-testid="friends-page">
      <Link
        to="/commons"
        data-testid="friends-back"
        className="inline-flex items-center gap-2 font-mono-lc text-[10px] tracking-[0.24em] uppercase text-[var(--cream-mute)] hover:text-[var(--brass)] transition-colors"
      >
        <ArrowLeft size={12} strokeWidth={1.5} />
        back to the commons
      </Link>

      <header className="text-center mt-8">
        <p className="font-mono-lc text-[10px] tracking-[0.24em] uppercase text-[var(--brass)] opacity-80">
          your circle
        </p>
        <h1 className="mt-3 font-serif-lit text-3xl sm:text-5xl text-[var(--cream)] leading-[1.05]">
          friends
        </h1>
        <p className="mt-3 font-serif-lit italic text-[var(--cream-dim)] text-base sm:text-lg">
          in alphabetical order, always
        </p>
      </header>

      <section className="mt-8">
        {loading ? (
          <p className="text-center py-12 font-mono-lc text-[10px] tracking-[0.24em] uppercase text-[var(--cream-mute)]" data-testid="friends-loading">
            opening the little black book…
          </p>
        ) : friends.length === 0 ? (
          <div className="text-center py-12" data-testid="friends-empty">
            <p className="font-serif-lit text-xl sm:text-2xl text-[var(--cream)]">no friends yet</p>
            <p className="mt-3 font-garamond italic text-[var(--cream-dim)] max-w-md mx-auto">
              browse the directory and send a request to someone you know. friends only appear here once they accept.
            </p>
            <Link
              to="/commons/directory"
              data-testid="friends-goto-directory"
              className="mt-6 lc-btn inline-flex items-center gap-2"
            >
              <UserPlus size={12} strokeWidth={1.8} />
              open the directory
            </Link>
          </div>
        ) : (
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="friends-list">
            {friends.map(f => (
              <li key={f.user_id}>
                <Link
                  to={`/u/${f.username}`}
                  data-testid={`friend-card-${f.username}`}
                  className="placed-card p-4 flex items-center gap-4 hover:border-[var(--brass)]"
                >
                  <div className="shrink-0">
                    {f.picture ? (
                      <img src={f.picture} alt="" className="w-12 h-12 rounded-full object-cover border border-[rgba(179,137,90,0.3)]" />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-[var(--bg-panel-hi)] border border-[rgba(179,137,90,0.3)] flex items-center justify-center font-serif-lit text-lg text-[var(--brass)]">
                        {(f.name || "?").slice(0, 1).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-serif-lit text-[var(--cream)] text-base truncate">{f.name}</p>
                    <p className="font-mono-lc text-[10px] tracking-[0.14em] text-[var(--cream-mute)] truncate">@{f.username}</p>
                    {f.bio && (
                      <p className="mt-1 font-garamond italic text-[var(--cream-dim)] text-sm line-clamp-1">{f.bio}</p>
                    )}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
